<template>
  <v-dialog v-model="computedIsVisible" persistent max-width="756">
    <v-card>
      <v-card-title class="headline">{{ $i18n.t('dashboard.filters.title') }}</v-card-title>
      <v-card-text>
        <v-container class="pa-0">
          <v-form ref="fieldFilterForm" v-model="isFilterValid">
            <template v-for="(filter, idx) in filters">
              <v-row :key="filter.id" no-gutters>
                <v-col cols="4" class="pa-0 pr-1">
                  <v-tooltip top :disabled="!filter.attribute">
                    <template v-slot:activator="{ on }">
                      <v-autocomplete
                        v-on="on"
                        outlined
                        v-model="filter.attribute"
                        :items="computedFields"
                        :item-text="`label_${$i18n.locale}`"
                        :return-object="true"
                        :filter="autocompleteFilter"
                        :rules="requiredRules"
                        :label="$i18n.t('dashboard.filters.attribute')"
                        :menu-props="{ minWidth: '300px', maxWidth: '300px' }"
                        @change="delete filter.subAttribute"
                      />
                    </template>
                    {{ filter.attribute[`label_${$i18n.locale}`] }}
                  </v-tooltip>
                </v-col>
                <template v-if="!filter.attribute.isRelated">
                  <v-col cols="3" class="pa-0 pr-1">
                    <v-select
                      outlined
                      v-model="filter.operator"
                      :disabled="!filter.attribute"
                      :items="getOperators(filter.attribute)"
                      :label="$i18n.t('dashboard.filters.operator')"
                      :rules="requiredRules"
                    />
                  </v-col>
                  <v-col cols="4" class="pa-0 pr-1">
                    <filter-autocomplete
                      :attribute.sync="filter.attribute"
                      :entity="entitiesType"
                      :idx="idx"
                      :value.sync="filter.value"
                      :public-hints="publicHints"
                      @resetValidation="resetValidation"
                    />
                  </v-col>
                  <v-col cols="1" class="pa-0 pr-1 pt-2">
                    <v-tooltip left>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="float-right"
                          fab
                          dark
                          small
                          :color="filters.length > 1 ? 'primary' : '#bababa'"
                          v-on="on"
                        >
                          <v-icon dark :disabled="filters.length < 2" @click="deleteFilter(idx)">mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{
                          filters.length > 1
                            ? $i18n.t('dashboard.filters.delete')
                            : $i18n.t('dashboard.filters.deleteOnlyElement')
                        }}
                      </span>
                    </v-tooltip>
                  </v-col>
                </template>
              </v-row>
              <v-row :key="`${idx}-subfield`" no-gutters v-if="filter.attribute.isRelated" style="position: relative">
                <v-col cols="4" class="pa-0 pr-1 sub-attribute-name">
                  <v-tooltip top :disabled="!filter.subAttribute">
                    <template v-slot:activator="{ on }">
                      <v-autocomplete
                        v-on="on"
                        outlined
                        v-model="filter.subAttribute"
                        :items="filter.attribute.subFields"
                        :item-text="`label_${$i18n.locale}`"
                        :return-object="true"
                        :filter="autocompleteFilter"
                        :rules="requiredRules"
                        :label="$i18n.t('dashboard.filters.subAttribute')"
                        :menu-props="{ minWidth: '300px', maxWidth: '300px' }"
                      />
                    </template>
                    {{ filter.subAttribute ? filter.subAttribute[`label_${$i18n.locale}`] : '' }}
                  </v-tooltip>
                </v-col>
                <v-col cols="3" class="pa-0 pr-1">
                  <v-select
                    outlined
                    v-model="filter.operator"
                    :disabled="!filter.subAttribute"
                    :items="getOperators(filter.subAttribute)"
                    :label="$i18n.t('dashboard.filters.operator')"
                    :rules="requiredRules"
                  />
                </v-col>
                <v-col cols="4" class="pa-0 pr-1">
                  <filter-autocomplete
                    :attribute.sync="filter.subAttribute || {}"
                    :entity="entitiesType"
                    :idx="idx"
                    :parent-attribute-name="filter.attribute.name"
                    :value.sync="filter.value"
                    :public-hints="publicHints"
                    @resetValidation="resetValidation"
                  />
                  </v-col>
                    <v-col cols="1" class="pa-0 pr-1 pt-2">
                      <v-tooltip left>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            class="float-right"
                            fab
                            dark
                            small
                            :color="filters.length > 1 ? 'primary' : '#bababa'"
                            v-on="on"
                          >
                            <v-icon dark :disabled="filters.length < 2" @click="deleteFilter(idx)">mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{
                            filters.length > 1
                              ? $i18n.t('dashboard.filters.delete')
                              : $i18n.t('dashboard.filters.deleteOnlyElement')
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
              </v-row>
              <v-row :key="`${idx}-operator`" no-gutters>
                <v-col cols="4" />
                <v-col v-if="filters.length > 1 && idx != filters.length - 1" cols="3" class="pa-0 pr-1">
                  <v-select
                    outlined
                    v-model="filter.mergeOperator"
                    :items="['OR', 'AND']"
                    :label="$i18n.t('dashboard.filters.operator')"
                    :menu-props="{ bottom: true }"
                  />
                </v-col>
              </v-row>
            </template>
          </v-form>
          <v-row no-gutters>
            <v-col cols="12" class="pa-0">
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <v-btn class="float-right" fab dark small color="primary" v-on="on" @click="addFilter">
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $i18n.t('dashboard.filters.add') }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1" />
        <v-btn
          color="primary"
          text
          :loading="loadingRun"
          :disabled="loadingClear || !isFilterValid"
          @click="runFilters"
        >
          {{ $i18n.t('dashboard.filters.filter') }}
        </v-btn>
        <v-btn color="primary" text :loading="loadingClear" :disabled="loadingRun" @click="clearFilters">
          {{ $i18n.t('dashboard.filters.clear') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import axios from 'axios';
import FilterAutocomplete from '@/components/fields/FilterAutocomplete';
import fieldFilter from '@/mixins/fieldFilter';
const capitalize = string => {
  return string[0].toUpperCase() + string.slice(1);
};
export default {
  name: 'FieldFilter',
  mixins: [fieldFilter],
  components: {
    FilterAutocomplete
  },
  props: {
    entities: {
      type: Array,
      required: true
    },
    entitiesType: {
      type: String,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    },
    publicHints: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    additionalRelatedAttributes() {
      return Object.keys(this.additionalRelatedAttributesFields);
    },
    computedFields() {
      return this.fields
        .map(field => {
          if (field.type === 'dicts_list' && field.related_to_api_name) {
            const { label_en, label_pl, name, type, fields, related_to_api_name, related_name_field } = field;
            return {
              label_en,
              label_pl,
              name,
              type,
              subFields: fields,
              apiName: related_to_api_name,
              relatedField: related_name_field,
              isRelated: true
            };
          } else if (field.type === 'dicts_list') {
            const { label_en, label_pl, name, type, fields } = field;
            return {
              label_en,
              label_pl,
              name,
              type,
              subFields: fields,
              isRelated: true
            };
          } else if (this.additionalRelatedAttributes.includes(field.type)) {
            const { label_en, label_pl, name, type } = field;
            const fields = this.additionalRelatedAttributesFields[field.type];
            return {
              label_en,
              label_pl,
              name,
              type,
              isRelated: true,
              subFields: fields,
            };
          }
          return { ...field, isRelated: false };
        })
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      }
    },
    filters: {
      get() {
        return this.$store.getters[`get${capitalize(this.entitiesType)}Filters`];
      },
      set(nV) {
        this.$store.commit(`set${capitalize(this.entitiesType)}Filters`, nV);
      }
    },
    hasValidFilters() {
      return this.filters.every(
        filter =>
          filter.attribute &&
          (!filter.attribute.isRelated || filter.subAttribute) &&
          (filter.value || filter.value == '0' || filter.value === false)
      );
    },
    requiredRules() {
      return [v => !!v || v == '0' || this.$i18n.t('default.requiredField')];
    }
  },
  data: () => ({
    isFilterValid: false,
    loadingClear: false,
    loadingRun: false
  }),
  methods: {
    addFilter() {
      this.$set(this.filters, this.filters.length, {
        attribute: '',
        operator: '==',
        value: '',
        mergeOperator: 'OR',
        id: JSON.parse(JSON.stringify(new Date().getTime())),
      });
    },
    autocompleteFilter(item, queryText, itemText) {
      return itemText.toLowerCase().includes(queryText);
    },
    clearFilters() {
      this.loadingClear = true;
      this.filters = [
        {
          attribute: '',
          operator: '==',
          value: '',
          mergeOperator: 'OR',
          id: JSON.parse(JSON.stringify(new Date().getTime())),
        }
      ];
      this.$refs.fieldFilterForm.resetValidation();
      this.$emit('setFilteredEntities', this.entities);
      this.computedIsVisible = false;
      this.loadingClear = false;
    },
    deleteFilter(idx) {
      this.filters.splice(idx, 1);
    },
    evaluateExpressions(expressionsArray, operator, isMultiselect = false, isRelated) {
      if (isMultiselect) {
        if (operator === 'LIKE') {
          return expressionsArray.some(expression => eval(expression));
        } else if (isRelated) {
          return expressionsArray.some(expression => eval(expression));
        } else {
          return expressionsArray.every(expression => eval(expression));
        }
      }
      if (operator === '!=') {
        return expressionsArray.every(expression => eval(expression));
      }
      return expressionsArray.some(expression => eval(expression));
    },
    async filter() {
      let apiObjects = {};
      let entities = [...this.entities];
      if (this.hasValidFilters) {
        this.loadingRun = true;
        let filteredIds = [];
        let lastMergeOperator = undefined;

        for (const [idx, filter] of this.filters.entries()) {
          const tempFiltered = [];
          const isRelated = filter.attribute.isRelated;
          const { operator, value } = filter;
          const { apiName, relatedField } = filter.attribute;
          const { translate, type } = filter[isRelated ? 'subAttribute' : 'attribute'];
          const name = `${filter.attribute.name}${isRelated ? `.${filter.subAttribute.name}` : ''}`;

          if (apiName && relatedField) {
            const r = await axios.get(`/${apiName}`);
            apiObjects[apiName] = r.data[apiName];
          }

          entities.forEach(entity => {
            let entityValue = this.getEntityValue(entity, name, type, isRelated, translate, apiObjects[apiName]);
            if (!entityValue && entityValue !== false && entityValue != '0') return;
            if (Array.isArray(entityValue) && entityValue.length < 1) return;

            let filterValue = '';
            if (Array.isArray(value) && translate) {
              filterValue = value.map(v => v[this.$i18n.locale]);
            } else if (typeof value === 'object' && !Array.isArray(value) && !!value) {
              filterValue = value[this.$i18n.locale];
            } else {
              filterValue = value;
            }
            if (Array.isArray(filterValue)) {
              filterValue = filterValue
                .sort((a, b) => {
                  return (a || '').toString().toLowerCase() > (b || '').toString().toLowerCase() ? 1 : -1;
                })
                .join(',');
            }

            const expressionsArray = this.getExpressionsArray(
              entityValue,
              filterValue,
              operator,
              type === 'multiselect'
            );

            try {
              if (this.evaluateExpressions(expressionsArray, operator, type === 'multiselect', isRelated)) {
                tempFiltered.push(entity.id);
              }
            } catch (error) {
              this.$root.$emit(
                'showSnackbar',
                this.$i18n.t('default.invalidValue', {
                  id: entity.id,
                  attributeName: this.$i18n.t(`${this.entitiesType}.table.${name}`)
                })
              );
            }
          });
          if (idx === 0 || lastMergeOperator === 'OR') {
            filteredIds = filteredIds.concat(tempFiltered);
          } else {
            filteredIds = filteredIds.filter(id => tempFiltered.indexOf(id) > -1);
          }
          lastMergeOperator = filter.mergeOperator;
        }
        entities = entities.filter(entity => filteredIds.includes(entity.id));
      }
      setTimeout(() => {
        this.loadingRun = false;
        this.$emit('setFilteredEntities', entities);
        this.computedIsVisible = false;
      }, 500);
    },
    getExpressionsArray(entityValue, filterValue, operator, isMultiselect = false) {
      const entityValueArray = Array.isArray(entityValue) ? entityValue : [entityValue];
      const filterValueQ = typeof filterValue === 'number' ? filterValue : `'${filterValue}'`;
      return entityValueArray.map(value => {
        const valueQ = typeof value === 'number' ? value : `'${value}'`;
        if (isMultiselect) {
          return operator === 'LIKE'
            ? `'${value}'.includes('${filterValue}')`
            : `${filterValueQ} ${operator} ${valueQ}`;
        } else {
          return operator === 'LIKE'
            ? `'${value}'.includes('${filterValue}')`
            : `${valueQ} ${operator} ${filterValueQ}`;
        }
      });
    },
    getEntityValue(entity, name, type, isRelated, translate, apiObjects) {
      const dict = {
        __wlasciciele: 'wlasciciele'
      };
      const dictKeys = Object.keys(dict);
      if (isRelated) {
        const [attributeName, subAttributeName] = name.split('.');
        let attributeValue = entity[dictKeys.includes(attributeName) ? dict[attributeName] : attributeName];
        if (Array.isArray(attributeValue)) {
          if (attributeValue.length < 1) {
            return [];
          }
          if (apiObjects) {
            attributeValue = attributeValue.map(aV => {
              if (!aV || (!aV.pl && !aV.en)) {
                return null;
              }
              return apiObjects.find(
                object => object.wlasciciel.pl + '-' + object.wlasciciel.en === aV.pl + '-' + aV.en
              );
            });
          }
          return attributeValue
            .map(aV => {
              if (!aV || !aV[subAttributeName]) {
                return null;
              }
              const subAttributeValue = aV[subAttributeName];
              if (type === 'multiselect') {
                return subAttributeValue
                  .map(sAV => (translate ? sAV[this.$i18n.locale] : sAV))
                  .sort((a, b) => {
                    return (a || '').toString().toLowerCase() > (b || '').toString().toLowerCase() ? 1 : -1;
                  })
                  .join(',');
              } else {
                return translate ? aV[subAttributeName][this.$i18n.locale] : aV[subAttributeName];
              }
            })
            .filter(arrayValue => !!arrayValue);
        } else {
          if (!attributeValue) {
            return undefined;
          }
          if (apiObjects) {
            attributeValue =
              !attributeValue || (!attributeValue.pl && !attributeValue.en)
                ? undefined
                : apiObjects.find(
                    object =>
                      object.wlasciciel.pl + '-' + object.wlasciciel.en === attributeValue.pl + '-' + attributeValue.en
                  );
          }
          return translate ? attributeValue[subAttributeName][this.$i18n.locale] : attributeValue[subAttributeName];
        }
      }

      let attributeValue = entity[dictKeys.includes(name) ? dict[name] : name];
      if (!attributeValue && attributeValue != '0' && attributeValue !== false) {
        return undefined;
      } else if (Array.isArray(attributeValue)) {
        return (translate ? attributeValue.map(aV => aV[this.$i18n.locale]) : attributeValue)
          .sort((a, b) => {
            return (a || '').toString().toLowerCase() > (b || '').toString().toLowerCase() ? 1 : -1;
          })
          .join(',');
      }
      return translate ? attributeValue[this.$i18n.locale] : attributeValue;
    },
    getOperators(attribute) {
      if (!attribute) {
        return [];
      }
      if (attribute.type === 'number') {
        return ['==', '!=', '>', '<', 'LIKE'];
      } else if (attribute.type === 'multiselect') {
        return ['==', 'LIKE'];
      } else if (attribute.type === 'select') {
        return ['==', '!='];
      } else if (attribute.type === 'boolean') {
        return ['=='];
      }
      return ['==', '!=', 'LIKE'];
    },
    runFilters() {
      this.filter();
    },
    resetValidation() {
      if (!this.$refs.fieldFilterForm) return;
      this.$refs.fieldFilterForm.resetValidation();
    },
    setFiltersBadge(value) {
      this.$emit(
        'setFiltersBadge',
        value.filter(
          filter =>
            filter.attribute &&
            filter.attribute.name &&
            (!filter.subAttribute || filter.subAttribute.name) &&
            filter.value
        ).length
      );
    },
  },
  watch: {
    entities() {
      this.filter();
    },
    filters: {
      deep: true,
      handler(nV) {
        this.setFiltersBadge(nV);
      }
    }
  },
  mounted() {
    this.setFiltersBadge(this.filters);
  }
};
</script>
<style lang="scss" scoped>
.sub-attribute-name::after {
  content: '';
  height: 30px;
  position: absolute;
  left: 17%;
  top: -30px;
  border-left: 2px dashed #bababa;
}
</style>